import { DocumentSource } from '../constants/appConstants';
const DocumentStatus = {
  // Common statuses
  QUEUED: 'QUEUED',
  SUBMITTED: 'SUBMITTED',
  MLS_VALIDATION_ERROR: 'MLS_VALIDATION_ERROR',
  MLS_C5_TRANSMISSION_ERROR: 'MLS_C5_TRANSMISSION_ERROR',
  MLS_SUCCESS: 'MLS_SUCCESS',
  SKIPPED: 'SKIPPED',

  // Sales-specific statuses
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  MLS_C3_TRANSMISSION_ERROR: 'MLS_C3_TRANSMISSION_ERROR',
  MLS_C4_TRANSMISSION_ERROR: 'MLS_C4_TRANSMISSION_ERROR',

  // Purchase-specific statuses
  MLS_SENT: 'MLS_SENT',
  MLS_RECEIVED: 'MLS_RECEIVED',
};

const OverallStatus = {
  // Common overall statuses
  TRANSMITTED: 'TRANSMITTED',
  REJECTED: 'REJECTED',
  APPROVED: 'APPROVED',
  PENDING: 'PENDING',

  // Sales-specific overall statuses
  VALIDATING: 'VALIDATING',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  PENDING_TRANSMISSION: 'PENDING_TRANSMISSION',

  // Purchase-specific overall statuses
  TRANSMITTING: 'TRANSMITTING',
};

function deriveSentDocumentsOverallStatus(c5Status, participantStatus) {
  // Internal configuration for sales
  const salesStatusHierarchy = {
    [DocumentStatus.VALIDATION_ERROR]: 0,
    [DocumentStatus.QUEUED]: 1,
    [DocumentStatus.SUBMITTED]: 2,
    [DocumentStatus.MLS_RECEIVED]: 3,
    [DocumentStatus.MLS_VALIDATION_ERROR]: 4,
    [DocumentStatus.MLS_C3_TRANSMISSION_ERROR]: 4,
    [DocumentStatus.MLS_C4_TRANSMISSION_ERROR]: 4,
    [DocumentStatus.MLS_C5_TRANSMISSION_ERROR]: 4,
    [DocumentStatus.MLS_SUCCESS]: 5,
    // Purchase-specific statuses not used in sales logic
    [DocumentStatus.MLS_SENT]: -1,
  };

  const salesMlsErrorStatuses = [
    DocumentStatus.MLS_VALIDATION_ERROR,
    DocumentStatus.MLS_C4_TRANSMISSION_ERROR,
    DocumentStatus.MLS_C5_TRANSMISSION_ERROR,
  ];

  // Rule 1: If no document status in one or both, return VALIDATING
  if (!c5Status || !participantStatus) {
    return OverallStatus.VALIDATING;
  }

  // Rule 5: If one of the document status is an MLS error, return REJECTED
  if (salesMlsErrorStatuses.includes(c5Status) || salesMlsErrorStatuses.includes(participantStatus)) {
    return OverallStatus.REJECTED;
  }

  // Rule 6: If both document statuses are MLS_SUCCESS, return APPROVED
  if (c5Status === DocumentStatus.MLS_SUCCESS && participantStatus === DocumentStatus.MLS_SUCCESS) {
    return OverallStatus.APPROVED;
  }

  // Handle C3 SKIPPED cases for any customer type
  if (participantStatus === DocumentStatus.SKIPPED) {
    // If C3 is skipped and C5 is success, return APPROVED
    if (c5Status === DocumentStatus.MLS_SUCCESS) {
      return OverallStatus.APPROVED;
    }
    // If C3 is skipped and C5 is an error, return REJECTED
    if (salesMlsErrorStatuses.includes(c5Status)) {
      return OverallStatus.REJECTED;
    }
    // If C3 is skipped and C5 is SUBMITTED or higher, return TRANSMITTED
    const c5StatusLevel = salesStatusHierarchy[c5Status];
    const submittedLevel = salesStatusHierarchy[DocumentStatus.SUBMITTED];
    if (c5StatusLevel >= submittedLevel) {
      return OverallStatus.TRANSMITTED;
    }
    // If C3 is skipped and C5 is QUEUED, return PENDING_TRANSMISSION
    if (c5Status === DocumentStatus.QUEUED) {
      return OverallStatus.PENDING_TRANSMISSION;
    }
  }

  // Rule 4: If both are SUBMITTED or one is higher than SUBMITTED (but not MLS errors), return TRANSMITTED
  const status1Level = salesStatusHierarchy[c5Status];
  const status2Level = salesStatusHierarchy[participantStatus];
  const submittedLevel = salesStatusHierarchy[DocumentStatus.SUBMITTED];

  if (
    (status1Level >= submittedLevel && status2Level >= submittedLevel) ||
    (status1Level >= submittedLevel && participantStatus === DocumentStatus.SUBMITTED) ||
    (status2Level >= submittedLevel && c5Status === DocumentStatus.SUBMITTED)
  ) {
    return OverallStatus.TRANSMITTED;
  }

  // Rule 3: If one status is >= QUEUED but the other is QUEUED, return PENDING_TRANSMISSION
  const queuedLevel = salesStatusHierarchy[DocumentStatus.QUEUED];

  if (
    (status1Level >= queuedLevel && participantStatus === DocumentStatus.QUEUED) ||
    (status2Level >= queuedLevel && c5Status === DocumentStatus.QUEUED)
  ) {
    return OverallStatus.PENDING_TRANSMISSION;
  }

  // Default fallback
  return OverallStatus.VALIDATING;
}

function deriveReceivedDocumentsOverallStatus(c5Status, participantStatus) {
  // Internal configuration for purchases
  const purchaseStatusHierarchy = {
    [DocumentStatus.QUEUED]: 1,
    [DocumentStatus.SUBMITTED]: 2,
    [DocumentStatus.MLS_SENT]: 3,
    [DocumentStatus.MLS_RECEIVED]: 4,
    [DocumentStatus.MLS_VALIDATION_ERROR]: 5,
    [DocumentStatus.MLS_C5_TRANSMISSION_ERROR]: 5,
    [DocumentStatus.MLS_SUCCESS]: 6,
    // Sales-specific statuses not used in purchase logic
    [DocumentStatus.VALIDATION_ERROR]: -1,
    [DocumentStatus.MLS_C4_TRANSMISSION_ERROR]: -1,
  };

  const purchaseMlsErrorStatuses = [DocumentStatus.MLS_VALIDATION_ERROR, DocumentStatus.MLS_C5_TRANSMISSION_ERROR];

  // Handle null/undefined cases - default to the lowest status for purchases
  const status1 = c5Status || DocumentStatus.QUEUED;
  const status2 = participantStatus || DocumentStatus.QUEUED;

  const status1Level = purchaseStatusHierarchy[status1];
  const status2Level = purchaseStatusHierarchy[status2];
  const queuedLevel = purchaseStatusHierarchy[DocumentStatus.QUEUED];
  const mlsSentLevel = purchaseStatusHierarchy[DocumentStatus.MLS_SENT];

  // Rule 1: If one status is >= QUEUED but the other is QUEUED, return TRANSMITTING
  if (
    (status1Level >= queuedLevel && status1Level <= mlsSentLevel && status2 === DocumentStatus.QUEUED) ||
    (status2Level >= queuedLevel && status2Level <= mlsSentLevel && status1 === DocumentStatus.QUEUED)
  ) {
    return OverallStatus.TRANSMITTING;
  }

  // For remaining rules, we need to check the "remaining" (non-QUEUED) status
  const remainingStatus = status1Level > status2Level ? status1 : status2;

  // Rule 4: If remaining status is MLS_SUCCESS, return APPROVED
  if (remainingStatus === DocumentStatus.MLS_SUCCESS) {
    return OverallStatus.APPROVED;
  }

  // Rule 3: If remaining status is MLS error, return REJECTED
  if (purchaseMlsErrorStatuses.includes(remainingStatus)) {
    return OverallStatus.REJECTED;
  }

  // Rule 2: If remaining status is SUBMITTED, MLS_SENT, or MLS_RECEIVED, return TRANSMITTED
  if (
    remainingStatus === DocumentStatus.SUBMITTED ||
    remainingStatus === DocumentStatus.MLS_SENT ||
    remainingStatus === DocumentStatus.MLS_RECEIVED
  ) {
    return OverallStatus.TRANSMITTED;
  }

  // Default fallback
  return OverallStatus.TRANSMITTING;
}

function parseDocumentStatus(status) {
  if (!status) return null;

  const enumValues = Object.values(DocumentStatus);
  return enumValues.find((enumValue) => enumValue === status) || null;
}

function deriveStatusFromMetadata(peppolData, isPurchase = false, isB2CCustomer = false, documentSource = null) {
  if (!peppolData) {
    return isPurchase ? OverallStatus.TRANSMITTING : OverallStatus.VALIDATING;
  }

  const c5Status = peppolData?.toC5?.status || null;

  let participantStatus =
    peppolData?.toC2?.status != null ? peppolData?.toC2?.status : peppolData?.toC3?.status || null;

  const validationError = peppolData?.validationResults;

  if (validationError && validationError.length > 0 && !c5Status && !participantStatus) {
    return OverallStatus.VALIDATION_FAILED;
  }

  if (isB2CCustomer && !isPurchase && !participantStatus) {
    participantStatus = DocumentStatus.SKIPPED;
  }

  // For B2C customers, derive status based on C5 status and C3 status (which can be SKIPPED)
  if (isB2CCustomer && !isPurchase) {
    return deriveSentDocumentsOverallStatus(parseDocumentStatus(c5Status), parseDocumentStatus(participantStatus));
  }

  if (!c5Status && !participantStatus) {
    return isPurchase ? OverallStatus.TRANSMITTING : OverallStatus.VALIDATING;
  }

  return documentSource === DocumentSource.PEPPOL
    ? deriveReceivedDocumentsOverallStatus(parseDocumentStatus(c5Status), parseDocumentStatus(participantStatus))
    : deriveSentDocumentsOverallStatus(parseDocumentStatus(c5Status), parseDocumentStatus(participantStatus));
}

export {
  DocumentStatus,
  OverallStatus,
  deriveStatusFromMetadata,
};
